import {
  Table as CTable,
  TableCell as CTableCell,
  TableRow as CTableRow,
  tableCellClasses
} from '@mui/material'
import { styled } from '@mui/material'

export const Table = styled(CTable)(() => ({
  '.MuiTableCell-root': {
    border: 0
  },
  boxShadow: '0',
  // Grow to the natural width of the columns instead of squeezing them; the
  // wrapping TableContainer provides the horizontal scroll when content
  // overflows. Narrow tables still fill the container (minWidth 100%).
  width: 'auto',
  minWidth: '100%',
  // Keep headers on a single line so each column claims a sensible min width
  // (this is what stops the "squeezed columns" effect). Body cells keep wrapping.
  [`& .${tableCellClasses.head}`]: {
    whiteSpace: 'nowrap'
  },
  // Scale MUI SvgIcons inside body cells with the table font-size preference.
  // clientbase <Icon> (FontAwesome `svg[data-icon]`, sized in `1em`) already
  // scales via the cell font cascade, so it's intentionally excluded here to
  // avoid double-scaling. `scale(1)` when the var is absent keeps every other
  // table — and the `md` default — pixel-identical.
  [`& .${tableCellClasses.body} .MuiSvgIcon-root`]: {
    transform: 'scale(var(--cb-table-icon-scale, 1))'
  },
  // O <Radio> do MUI mantém o miolo sempre no DOM e usa `transform: scale(0|1)`
  // para alternar entre desmarcado e marcado. A regra acima é mais específica e
  // vencia esse scale, então todo radio desmarcado nascia preenchido — parecia
  // selecionado. Aqui devolvemos o comportamento: fundo sempre visível, miolo
  // só quando `Mui-checked`.
  [`& .${tableCellClasses.body} .MuiRadio-root .MuiSvgIcon-root`]: {
    transform: 'scale(1)'
  },
  [`& .${tableCellClasses.body} .MuiRadio-root:not(.Mui-checked) .MuiSvgIcon-root + .MuiSvgIcon-root`]:
    {
      transform: 'scale(0)'
    }
}))

// Font size and vertical padding are driven by CSS custom properties set on a
// table ancestor (the scroll wrapper) from the per-table display preferences.
// Falling back to the historical values keeps untouched tables identical.
const CELL_PADDING =
  'var(--cb-table-cell-pt, 8px) 4px var(--cb-table-cell-pb, 7px)'
const CELL_FONT_SIZE = 'var(--cb-table-font-size, 14px)'

export const TableHeadCell = styled(CTableCell)(({ theme }) => ({
  cursor: 'default',
  [`&.${tableCellClasses.head}`]: {
    color: theme.palette.neutral['500'],
    fontWeight: '600',
    fontSize: CELL_FONT_SIZE,
    padding: CELL_PADDING
  },
  [`&.${tableCellClasses.body}`]: {
    border: 0,
    boxShadow: 0,
    fontSize: CELL_FONT_SIZE,
    padding: CELL_PADDING
  }
}))

type TableCellProps = {
  clickable?: boolean
}

export const TableCell = styled(CTableCell)<TableCellProps>(
  ({ theme, clickable = true }) => ({
    cursor: clickable ? 'pointer' : 'auto',
    [`&.${tableCellClasses.head}`]: {
      color: theme.palette.neutral['500'],
      fontWeight: '600',
      fontSize: CELL_FONT_SIZE,
      padding: CELL_PADDING
    },
    [`&.${tableCellClasses.body}`]: {
      border: 0,
      boxShadow: 0,
      fontSize: CELL_FONT_SIZE,
      padding: CELL_PADDING
    }
  })
)

type TableRowProps = {
  hoverInRow?: boolean
  clickable?: boolean
  isLastRow?: boolean
  hideLastRowSeparator?: boolean
}

export const TableRow = styled(CTableRow)<TableRowProps>(
  ({
    theme,
    hoverInRow,
    clickable = true,
    isLastRow,
    hideLastRowSeparator
  }) => ({
    '&:hover': {
      ...(hoverInRow &&
        clickable && { background: `${theme.palette.primary.main}10` })
    },
    '& td, & th': {
      borderBottom:
        isLastRow && hideLastRowSeparator
          ? 'none !important'
          : '1px solid #E3E6E3 !important'
    }
  })
)

export const TableNameWrapper = styled('div')(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  gap: 11,
  [`@media (max-width: ${theme.breakpoints.values.lg}px)`]: {
    alignItems: 'flex-start',
    flexDirection: 'column',
    gap: 1
  }
}))

export const TableNickname = styled('span')(({ theme }) => ({
  color: theme.palette.neutral[400],
  fontSize: 12
}))
